import { Type } from '@sinclair/typebox';
import { createPathGuidParameter } from '../../routes/createPathParameter';
import { okResponse } from '../../routes/defaultResponses';
import { makeNullable } from '../../utils/typeboxHelpers';
import { sortColumnsSchema } from '../sortColumnsSchema';

export const flightLogPaths = {
    '/api/flightLog': {
        post: {
            operationId: 'flightLogControllers.create',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                id: Type.String(),
                                userId: Type.String(),
                                clientCompanyId: Type.Optional(Type.String()),
                                equipmentId: Type.Optional(Type.String()),
                                date: Type.String(),
                                startTime: Type.Optional(makeNullable(Type.String())),
                                endTime: Type.Optional(makeNullable(Type.String())),
                                flightDuration: Type.Number(),
                                remarks: Type.Optional(makeNullable(Type.String())),
                                surveyId: Type.Optional(Type.String()),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            id: Type.Optional(Type.String()),
                            userId: Type.String(),
                            clientCompanyId: Type.Optional(Type.String()),
                            equipmentId: Type.String(),
                            date: Type.String(),
                            startTime: Type.Optional(makeNullable(Type.String())),
                            endTime: Type.Optional(makeNullable(Type.String())),
                            flightDuration: Type.Number(),
                            remarks: Type.Optional(makeNullable(Type.String())),
                            surveyId: Type.Optional(Type.String()),
                        }),
                    },
                },
            },
        },
    },
    '/api/flightLog/{flightLogId}': {
        parameters: [createPathGuidParameter('flightLogId')],
        post: {
            operationId: 'flightLogControllers.update',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                id: Type.Optional(Type.String()),
                                userId: Type.String(),
                                clientCompanyId: Type.Optional(Type.String()),
                                equipmentId: Type.Optional(Type.String()),
                                date: Type.String(),
                                startTime: Type.Optional(makeNullable(Type.String())),
                                endTime: Type.Optional(makeNullable(Type.String())),
                                flightDuration: Type.Number(),
                                remarks: Type.Optional(makeNullable(Type.String())),
                                surveyId: Type.Optional(Type.String()),
                            }),
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            id: Type.Optional(Type.String()),
                            userId: Type.String(),
                            clientCompanyId: Type.Optional(Type.String()),
                            equipmentId: Type.Optional(Type.String()),
                            date: Type.String(),
                            startTime: Type.Optional(makeNullable(Type.String())),
                            endTime: Type.Optional(makeNullable(Type.String())),
                            flightDuration: Type.Number(),
                            remarks: Type.Optional(makeNullable(Type.String())),
                            surveyId: Type.Optional(Type.String()),
                        }),
                    },
                },
            },
        },
        get: {
            operationId: 'flightLogControllers.getOne',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Optional(
                                Type.Intersect([
                                    Type.Object({
                                        id: Type.String(),
                                    }),
                                    Type.Object({
                                        date: Type.String(),
                                    }),
                                    Type.Object({
                                        startTime: makeNullable(Type.String()),
                                    }),
                                    Type.Object({
                                        endTime: makeNullable(Type.String()),
                                    }),
                                    Type.Object({
                                        flightDuration: Type.Number(),
                                    }),
                                    Type.Object({
                                        remarks: makeNullable(Type.String()),
                                    }),
                                ]),
                            ),
                        },
                    },
                },
            },
        },
        delete: {
            operationId: 'flightLogControllers.remove',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                ok: Type.Boolean(),
                            }),
                        },
                    },
                },
            },
        },
    },
    '/api/equipment/{equipmentId}/flightLog': {
        parameters: [createPathGuidParameter('equipmentId')],
        get: {
            operationId: 'flightLogControllers.getAllForEquipment',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                total: Type.Number(),
                                items: Type.Array(
                                    Type.Intersect([
                                        Type.Object({
                                            id: Type.String(),
                                        }),
                                        Type.Object({
                                            userId: Type.String(),
                                        }),
                                        Type.Object({
                                            date: Type.String(),
                                        }),
                                        Type.Object({
                                            startTime: makeNullable(Type.String()),
                                        }),
                                        Type.Object({
                                            endTime: makeNullable(Type.String()),
                                        }),
                                        Type.Object({
                                            flightDuration: Type.Number(),
                                        }),
                                        Type.Object({
                                            remarks: makeNullable(Type.String()),
                                        }),
                                        Type.Object({
                                            users: Type.Object({
                                                firstName: Type.String(),
                                            }),
                                        }),
                                        Type.Object({
                                            users: Type.Object({
                                                lastName: Type.String(),
                                            }),
                                        }),
                                        Type.Object({
                                            survey: Type.Object({
                                                object: Type.Object({
                                                    name: Type.String(),
                                                }),
                                            }),
                                        }),
                                        Type.Object({
                                            survey: Type.Object({
                                                object: Type.Object({
                                                    ownerCompany: Type.Object({
                                                        name: Type.String(),
                                                    }),
                                                }),
                                            }),
                                        }),
                                    ]),
                                ),
                            }),
                        },
                    },
                },
            },
            parameters: [
                { name: 'limit', in: 'query', schema: Type.String(), required: true },
                { name: 'offset', in: 'query', schema: Type.String(), required: true },
                { name: 'sortColumns', in: 'query', schema: sortColumnsSchema, required: true },
            ],
        },
    },
    '/api/flightLogUser': {
        get: {
            operationId: 'flightLogControllers.getAllForUser',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                total: Type.Number(),
                                items: Type.Array(
                                    Type.Intersect([
                                        Type.Object({
                                            id: Type.String(),
                                        }),
                                        Type.Object({
                                            date: Type.String(),
                                        }),
                                        Type.Object({
                                            startTime: makeNullable(Type.String()),
                                        }),
                                        Type.Object({
                                            endTime: makeNullable(Type.String()),
                                        }),
                                        Type.Object({
                                            flightDuration: Type.Number(),
                                        }),
                                        Type.Object({
                                            remarks: makeNullable(Type.String()),
                                        }),
                                        Type.Object({
                                            equipment: Type.Object({
                                                id: Type.String(),
                                            }),
                                        }),
                                        Type.Object({
                                            equipment: Type.Object({
                                                name: Type.String(),
                                            }),
                                        }),
                                        Type.Object({
                                            survey: Type.Object({
                                                id: Type.String(),
                                            }),
                                        }),
                                        Type.Object({
                                            survey: Type.Object({
                                                object: Type.Object({
                                                    name: Type.String(),
                                                }),
                                            }),
                                        }),
                                        Type.Object({
                                            survey: Type.Object({
                                                object: Type.Object({
                                                    ownerCompany: Type.Object({
                                                        name: Type.String(),
                                                    }),
                                                }),
                                            }),
                                        }),
                                    ]),
                                ),
                            }),
                        },
                    },
                },
            },
            parameters: [
                { name: 'limit', in: 'query', schema: Type.String(), required: true },
                { name: 'offset', in: 'query', schema: Type.String(), required: true },
                { name: 'sortColumns', in: 'query', schema: sortColumnsSchema, required: true },
            ],
        },
    },
    '/api/surveys/{surveyId}/flightlogs': {
        parameters: [createPathGuidParameter('surveyId')],
        get: {
            operationId: 'flightLogControllers.getAllForSurvey',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                total: Type.Number(),
                                items: Type.Array(
                                    Type.Intersect([
                                        Type.Object({
                                            surveyId: Type.String(),
                                        }),
                                        Type.Object({
                                            id: Type.String(),
                                        }),
                                        Type.Object({
                                            userId: Type.String(),
                                        }),
                                        Type.Object({
                                            equipmentId: Type.String(),
                                        }),
                                        Type.Object({
                                            date: Type.String(),
                                        }),
                                        Type.Object({
                                            startTime: makeNullable(Type.String()),
                                        }),
                                        Type.Object({
                                            endTime: makeNullable(Type.String()),
                                        }),
                                        Type.Object({
                                            flightDuration: Type.Number(),
                                        }),
                                        Type.Object({
                                            remarks: makeNullable(Type.String()),
                                        }),
                                        Type.Object({
                                            users: Type.Object({
                                                firstName: Type.String(),
                                            }),
                                        }),
                                        Type.Object({
                                            users: Type.Object({
                                                lastName: Type.String(),
                                            }),
                                        }),
                                        Type.Object({
                                            equipment: Type.Object({
                                                name: Type.String(),
                                            }),
                                        }),
                                        Type.Object({
                                            survey: Type.Object({
                                                object: Type.Object({
                                                    name: Type.String(),
                                                }),
                                            }),
                                        }),
                                        Type.Object({
                                            survey: Type.Object({
                                                object: Type.Object({
                                                    ownerCompany: Type.Object({
                                                        name: Type.String(),
                                                    }),
                                                }),
                                            }),
                                        }),
                                    ]),
                                ),
                            }),
                        },
                    },
                },
            },
            parameters: [
                { name: 'limit', in: 'query', schema: Type.String(), required: true },
                { name: 'offset', in: 'query', schema: Type.String(), required: true },
                { name: 'sortColumns', in: 'query', schema: sortColumnsSchema, required: true },
            ],
        },
    },
    '/api/surveys/{surveyId}/flightLogs/deleteAll': {
        parameters: [createPathGuidParameter('surveyId')],
        delete: { operationId: 'flightLogControllers.removeSurveysAllFlightLogs', responses: { ...okResponse } },
    },
} as const;
